/* assets/waiver.js */

/**
 * Spirit Peaks Raceway - Waiver Form
 * Signature pad and form handling
 */

(function () {
  'use strict';

  // DOM Elements
  const form = document.getElementById('WaiverForm');
  const canvas = document.getElementById('WaiverSignature');
  const clearBtn = document.getElementById('WaiverClearSig');
  const signatureDataInput = document.getElementById('WaiverSignatureData');
  const signedAtInput = document.getElementById('WaiverSignedAt');
  const signatureError = document.getElementById('WaiverSignatureError');
  const submitBtn = document.getElementById('WaiverSubmitBtn');

  if (!form || !canvas || !signatureDataInput) {
    console.warn('Waiver form elements not found');
    return;
  }

  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  
  // State
  let isDrawing = false;
  let lastPoint = { x: 0, y: 0 };
  let hasSignature = false;
  let canvasRatio = 1;

  // Store signature data for resize recovery
  let signatureImageData = null;

  /**
   * Initialize canvas with proper scaling for HiDPI displays
   */
  function initCanvas() {
    const rect = canvas.getBoundingClientRect();
    canvasRatio = Math.max(window.devicePixelRatio || 1, 1);
    
    // Store existing signature if any
    if (hasSignature && canvas.width > 0 && canvas.height > 0) {
      signatureImageData = canvas.toDataURL('image/png');
    }
    
    // Set canvas dimensions
    canvas.width = Math.floor(rect.width * canvasRatio);
    canvas.height = Math.floor(rect.height * canvasRatio);
    
    // Scale context for HiDPI
    ctx.setTransform(canvasRatio, 0, 0, canvasRatio, 0, 0);
    
    // Set drawing styles
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111111';
    
    // Restore signature if we had one
    if (signatureImageData) {
      const img = new Image();
      img.onload = function() {
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
      };
      img.src = signatureImageData;
    }
  }

  /**
   * Get pointer position relative to canvas
   */
  function getPointerPosition(e) {
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  }

  /**
   * Start drawing
   */
  function startDrawing(e) {
    isDrawing = true;
    lastPoint = getPointerPosition(e);
    
    // Draw a dot for single clicks/taps
    ctx.beginPath();
    ctx.arc(lastPoint.x, lastPoint.y, ctx.lineWidth / 2, 0, Math.PI * 2);
    ctx.fill();
    
    hasSignature = true;
    hideSignatureError();
    
    e.preventDefault();
  }

  /**
   * Continue drawing
   */
  function draw(e) {
    if (!isDrawing) return;
    
    const currentPoint = getPointerPosition(e);
    
    ctx.beginPath();
    ctx.moveTo(lastPoint.x, lastPoint.y);
    ctx.lineTo(currentPoint.x, currentPoint.y);
    ctx.stroke();
    
    lastPoint = currentPoint;
    e.preventDefault();
  }

  /**
   * Stop drawing
   */
  function stopDrawing() {
    if (isDrawing) {
      isDrawing = false;
      // Update stored signature data
      signatureImageData = canvas.toDataURL('image/png');
    }
  }

  /**
   * Clear the signature canvas
   */
  function clearSignature() {
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
    signatureDataInput.value = '';
    signatureImageData = null;
    hasSignature = false;
    hideSignatureError();
  }

  /**
   * Check if canvas has any drawn content
   */
  function canvasHasContent() {
    // Use actual canvas pixel dimensions
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    // Check alpha channel for any non-transparent pixels
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] > 0) {
        return true;
      }
    }
    return false;
  }

  /**
   * Show signature error
   */
  function showSignatureError() {
    if (signatureError) {
      signatureError.hidden = false;
      canvas.classList.add('has-error');
    }
  }

  /**
   * Hide signature error
   */
  function hideSignatureError() {
    if (signatureError) {
      signatureError.hidden = true;
      canvas.classList.remove('has-error');
    }
  }

  /**
   * Set loading state on submit button
   */
  function setLoading(loading) {
    if (!submitBtn) return;
    
    const textEl = submitBtn.querySelector('.waiver-submit__text');
    const loadingEl = submitBtn.querySelector('.waiver-submit__loading');
    
    submitBtn.disabled = loading;
    
    if (textEl) textEl.hidden = loading;
    if (loadingEl) loadingEl.hidden = !loading;
  }

  /**
   * Compress signature image to reduce size
   * Shopify contact forms can have issues with very large base64 strings
   */
  function getCompressedSignature() {
    // Create a smaller canvas for the signature
    const maxWidth = 600;
    const rect = canvas.getBoundingClientRect();
    const scale = Math.min(1, maxWidth / rect.width);
    
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = Math.floor(rect.width * scale);
    tempCanvas.height = Math.floor(rect.height * scale);
    
    const tempCtx = tempCanvas.getContext('2d');
    tempCtx.fillStyle = '#ffffff';
    tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
    tempCtx.drawImage(canvas, 0, 0, tempCanvas.width, tempCanvas.height);
    
    // Use JPEG for smaller file size
    return tempCanvas.toDataURL('image/jpeg', 0.8);
  }

  /**
   * Handle form submission
   */
  function handleSubmit(e) {
    // Validate signature
    if (!canvasHasContent()) {
      e.preventDefault();
      showSignatureError();
      canvas.focus();
      return;
    }

    // Set signature data
    signatureDataInput.value = getCompressedSignature();
    
    // Set timestamp
    if (signedAtInput) {
      signedAtInput.value = new Date().toISOString();
    }

    // Show loading state
    setLoading(true);
  }

  /**
   * Debounce function for resize handling
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Initialize canvas
  initCanvas();

  // Mouse events
  canvas.addEventListener('mousedown', startDrawing);
  canvas.addEventListener('mousemove', draw);
  window.addEventListener('mouseup', stopDrawing);
  canvas.addEventListener('mouseleave', stopDrawing);

  // Touch events
  canvas.addEventListener('touchstart', startDrawing, { passive: false });
  canvas.addEventListener('touchmove', draw, { passive: false });
  window.addEventListener('touchend', stopDrawing);
  window.addEventListener('touchcancel', stopDrawing);

  // Clear button
  if (clearBtn) {
    clearBtn.addEventListener('click', clearSignature);
  }

  // Form submission
  form.addEventListener('submit', handleSubmit);

  // Handle window resize with debounce
  const debouncedResize = debounce(function() {
    initCanvas();
  }, 250);
  
  window.addEventListener('resize', debouncedResize);

  // Prevent iOS zoom on input focus
  document.querySelectorAll('.waiver-field input').forEach(function(input) {
    input.addEventListener('focus', function() {
      if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
        this.style.fontSize = '16px';
      }
    });
  });

  // Set default date to today
  const dateInput = document.getElementById('WaiverDate');
  if (dateInput && !dateInput.value) {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    dateInput.value = `${yyyy}-${mm}-${dd}`;
  }

  // Keyboard accessibility for canvas
  canvas.setAttribute('tabindex', '0');
  canvas.setAttribute('role', 'application');
  canvas.setAttribute('aria-label', 'Signature pad. Use mouse or touch to sign.');

})();